// One company's theme: the default every new show starts with, and what each
// of its shows wears.
//
// PORTED FROM THE OCTOBER HAND-OFF'S console-clients.jsx (ClientTheme). The
// prototype read a localStorage default; here it is `client_theme_default`,
// through GET and PUT /v1/admin/client-themes (#199), and the library is the
// themes that client may wear: Nova's own and its own, never another client's.
//
// THE DEFAULT IS A FALLBACK, NOT A STAMP. A show with no event theme of its own
// follows its client's default on every read, by resolveShowTheme, so changing
// the default changes what every such show wears on its next publish. A show
// that has chosen a theme keeps it. The table says which is which, so changing
// the select is not a surprise to anybody reading it first.
//
// MODEST ABOUT REQUESTS. What each show wears is one read per show, and a
// client with forty shows is forty reads for one table. So the first
// SHOW_READS are asked and the rest say where to look. The directory itself is
// the one Clients and Choose already read.
//
// A THEME OPENED FROM HERE has no show around it, and the theme page says so
// where a show's page has its switches.

import { useEffect, useState, type ReactNode } from "react";
import { NoticeBox, PageHead, Setting } from "@nova/desk-ui/parts";
import { Empty, SecLabel } from "@nova/desk-ui/kit";
import { Loading } from "@nova/desk-ui/states";
import type { ActivationDirectory, ActivationLine } from "../../../operator/src/activations.js";
import { listClients } from "../api/admin.js";
import { readActivations, type ApiOptions } from "../api/client.js";
import {
  createLibraryTheme,
  listLibrary,
  readClientDefault,
  readShowThemes,
  sameId,
  saveClientDefault,
  type LibraryRecord,
  type ShowThemes,
} from "../api/themes.js";
import type { SessionToken } from "../session/store.js";
import { blankTheme, nameOf, optionValue, pointable } from "../themes/model.js";
import { activationName } from "./activationName.js";
import { useConsoleToast } from "./Chrome.js";
import { lifecycle } from "./lifecycle.js";
import { putTheme, ThemeDetail, ThemeRefused, type ThemeRefusal } from "./Themes.js";

/** How many shows' themes are read for the table. See the header. */
export const SHOW_READS = 12;

export type ClientThemeProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly clientId: string;
  readonly themeId: string | null;
  readonly onOpenTheme: (themeId: string | null) => void;
  readonly onSessionEnded: (detail: string) => void;
};

type Read =
  | { readonly status: "reading" }
  | { readonly status: "failed"; readonly refusal: ThemeRefusal }
  | {
      readonly status: "ready";
      readonly name: string | null;
      readonly defaultId: string | null;
      readonly themes: readonly LibraryRecord[];
      readonly shows: readonly ActivationLine[];
      /** By activation id; absent where the show was not read (past SHOW_READS) or its read failed. */
      readonly wears: Readonly<Record<string, ShowThemes>>;
    };

export function ClientTheme({ api, token, clientId, themeId, onOpenTheme, onSessionEnded }: ClientThemeProps): ReactNode {
  const say = useConsoleToast();
  const [read, setRead] = useState<Read>({ status: "reading" });
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ThemeRefusal | null>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fail = (refused: ThemeRefusal & { readonly detail: string }): void => {
      if (refused.kind === "session_ended") onSessionEnded(refused.detail);
      setRead({ status: "failed", refusal: refused });
    };
    void (async () => {
      const clients = await listClients(api, token);
      if (cancelled) return;
      if (!clients.ok) return fail(clients);
      const name = clients.value.clients.find((c) => sameId(c.clientId, clientId))?.name ?? null;

      const fallback = await readClientDefault(api, token, clientId);
      if (cancelled) return;
      if (!fallback.ok) return fail(fallback);

      const themes = await listLibrary(api, token, clientId);
      if (cancelled) return;
      if (!themes.ok) return fail(themes);

      const directory = await readActivations<ActivationDirectory>(api, token);
      if (cancelled) return;
      // A failed read is said as one, not drawn as a client with no shows
      // (Bugbot on #203).
      if (!directory.ok) {
        return fail(
          directory.kind === "session_ended"
            ? { kind: "session_ended", detail: directory.detail }
            : { kind: "local", detail: `This client's shows could not be read: ${directory.detail}` },
        );
      }
      const shows = (directory.value.activations ?? []).filter((row) => sameId(row.client.client_id, clientId));

      const wears: Record<string, ShowThemes> = {};
      for (const show of shows.slice(0, SHOW_READS)) {
        const got = await readShowThemes(api, token, show.activation_id);
        if (cancelled) return;
        if (got.ok) wears[show.activation_id] = got.value;
        else if (got.kind === "session_ended") return fail(got);
      }

      setRead({ status: "ready", name, defaultId: fallback.value.themeId, themes: themes.value, shows, wears });
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, clientId, onSessionEnded, generation]);

  const title = read.status === "ready" && read.name !== null ? `${read.name} · theme` : "Client · theme";

  if (themeId !== null) {
    return (
      <ThemeDetail
        key={themeId}
        api={api}
        token={token}
        themeId={themeId}
        read={read.status === "ready" ? { status: "ready", show: null, themes: read.themes } : read}
        onShow={() => {}}
        onLibrary={(change) => {
          if (change.kind === "reload" || read.status !== "ready") {
            // As on the show's library: nothing stale is drawn while it is read again.
            setRead({ status: "reading" });
            setGeneration((n) => n + 1);
          }
          else setRead((now) => (now.status === "ready" ? { ...now, themes: putTheme(now.themes, change.theme) } : now));
        }}
        onBack={() => onOpenTheme(null)}
        onOpen={(id) => onOpenTheme(id)}
        onSessionEnded={onSessionEnded}
        crumb={title}
        clientId={clientId}
        clientName={read.status === "ready" ? read.name : null}
        wornElsewhere={read.status === "ready" ? wearersAtClient(read, themeId) : []}
      />
    );
  }

  const setDefault = async (next: string | null, receipt: (name: string | null) => string): Promise<boolean> => {
    if (read.status !== "ready") return false;
    setBusy(true);
    setRefusal(null);
    const saved = await saveClientDefault(api, token, { clientId, themeId: next });
    setBusy(false);
    if (!saved.ok) {
      if (saved.kind === "session_ended") onSessionEnded(saved.detail);
      setRefusal(saved);
      return false;
    }
    // From the state as it now is, not the read this function closed over: New
    // theme for X put the made theme in the list a moment ago (Bugbot on #203).
    setRead((now) => (now.status === "ready" ? { ...now, defaultId: saved.value.themeId } : now));
    say(receipt(read.name));
    return true;
  };

  const freshFor = async (): Promise<void> => {
    if (read.status !== "ready") return;
    setBusy(true);
    setRefusal(null);
    const made = await createLibraryTheme(api, token, { ...blankTheme(read.name ?? "Client theme"), clientId });
    setBusy(false);
    if (!made.ok) {
      if (made.kind === "session_ended") onSessionEnded(made.detail);
      setRefusal(made);
      return;
    }
    setRead((now) => (now.status === "ready" ? { ...now, themes: putTheme(now.themes, made.value) } : now));
    // The prototype makes the new theme the client's default in the same
    // move, and so does this: "New theme for X" is the client's palette.
    const pointed = await setDefault(made.value.themeId, (name) => `${made.value.name} is ${name ?? "this client"}’s default.`);
    if (pointed) onOpenTheme(made.value.themeId);
  };

  return (
    <div className="stack">
      <PageHead title={title} lede="The theme every new show for this client starts with." explain="info" />
      {read.status === "reading" ? <Loading rows={3} cols={3} label="Loading the client’s themes…" /> : null}
      {read.status === "failed" ? <ThemeRefused refusal={read.refusal} /> : null}
      {refusal === null ? null : <ThemeRefused refusal={refusal} />}
      {read.status === "ready" ? (
        <>
          {read.name === null ? (
            <NoticeBox kind="quiet" title="Client not found">
              <p>Check the link or ask Nova to grant this account access.</p>
              <p className="id clients-uuid">{clientId}</p>
            </NoticeBox>
          ) : null}
          <section>
            <SecLabel>Default theme</SecLabel>
            <div className="panel panel-stack">
              <Setting label="New shows start with" info="A show can still choose its own theme on This show → Themes.">
                <select
                  className="input"
                  name="client-default-theme"
                  value={read.defaultId === null ? "" : optionValue(read.themes, read.defaultId)}
                  disabled={busy}
                  onChange={(event) => {
                    const value = event.target.value === "" ? null : event.target.value;
                    void setDefault(value, (name) =>
                      value === null
                        ? `${name ?? "This client"} has no default theme.`
                        : `${name ?? "This client"}’s default is ${nameOf(read.themes, value) ?? "that theme"}.`,
                    );
                  }}
                >
                  <option value="">None · each app on its own system</option>
                  {pointable(read.themes).map((t) => (
                    <option key={t.themeId} value={t.themeId}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Setting>
              <div className="acts acts-flush">
                {read.defaultId === null ? null : (
                  <button type="button" className="btn btn-quiet" onClick={() => onOpenTheme(read.defaultId)}>
                    Edit {nameOf(read.themes, read.defaultId) ?? "the default"}
                  </button>
                )}
                <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void freshFor()}>
                  New theme for {read.name ?? "this client"}
                </button>
              </div>
            </div>
          </section>

          <section>
            <SecLabel info="What each show wears now. A show with no event theme of its own follows this client's default, so changing the default above changes those shows the next time they are published.">
              {`Shows · ${read.shows.length}`}
            </SecLabel>
            {read.shows.length === 0 ? (
              <Empty title="No accessible activations">
                <p>No activation this account can open belongs to this client.</p>
              </Empty>
            ) : (
              <div className="tablewrap">
                <div className="tablescroll">
                  <table className="slim">
                    <thead>
                      <tr>
                        <th scope="col">Show</th>
                        <th scope="col">Status</th>
                        <th scope="col">Wears</th>
                      </tr>
                    </thead>
                    <tbody>
                      {read.shows.map((show) => {
                        const heading = activationName(show.name);
                        return (
                          <tr key={show.activation_id}>
                            <td data-label="Show">
                              <span className={heading.unnamed ? "quiet" : undefined}>{heading.text}</span>
                            </td>
                            <td data-label="Status">{lifecycle(show)}</td>
                            <td data-label="Wears" className="quiet">
                              {wornBy(read.wears[show.activation_id], read.themes, read.defaultId)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {read.shows.length > SHOW_READS ? (
              <p className="help help-head">
                The first {SHOW_READS} shows are read here. The others say what they wear on their own Themes page.
              </p>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}

/**
 * The Wears cell. The show's own read where there is one; the client's default
 * is read off this page's own state rather than the show's, so the table
 * agrees with the select above it the moment the default changes.
 */
function wornBy(show: ShowThemes | undefined, themes: readonly LibraryRecord[], defaultId: string | null): string {
  if (show === undefined) return "Not read here";
  if (show.showThemeId !== null) return `${nameOf(themes, show.showThemeId) ?? "Another theme"} · event theme`;
  if (defaultId !== null) return `${nameOf(themes, defaultId) ?? "The default"} · client default`;
  return "Its own system";
}

/**
 * Who wears a theme across a client: the client's default, and each of its
 * shows read here that wears it on any app. What a delete from this page has
 * to warn about, since there is no single show to ask.
 */
function wearersAtClient(
  read: { readonly defaultId: string | null; readonly shows: readonly { readonly activation_id: string; readonly name?: unknown }[]; readonly wears: Readonly<Record<string, ShowThemes>> },
  themeId: string,
): string[] {
  const out: string[] = [];
  if (read.defaultId !== null && sameId(read.defaultId, themeId)) out.push("The client's default");
  for (const show of read.shows) {
    const wears = read.wears[show.activation_id];
    if (wears !== undefined && wears.surfaces.some((s) => s.themeId !== null && sameId(s.themeId, themeId))) {
      out.push(activationName(show.name as never).text);
    }
  }
  return out;
}
